import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBooking } from "./BookingModal";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Technology", href: "#technology" },
  { label: "Services", href: "#services" },
  { label: "Impact", href: "#impact" },
  { label: "Learn", href: "#learning" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openBooking } = useBooking();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/85 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <span className="font-serif text-2xl tracking-tight text-[#F3F3F1]">
            Ai<span className="text-cyan-400">.</span>Sho
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              <button
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                data-testid="navbar-dashboard-btn"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-cyan-400 transition-colors"
              >
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </button>
              <button
                onClick={logout}
                data-testid="navbar-logout-btn"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-cyan-400 transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="navbar-login-btn"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-cyan-400 transition-colors"
            >
              Client Portal
            </button>
          )}
          <button
            onClick={openBooking}
            data-testid="navbar-cta"
            className="group flex items-center gap-1.5 border border-white/20 hover:border-cyan-400 hover:bg-cyan-400 hover:text-black text-white px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300"
          >
            Book a Call
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#050505] border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-white/70"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => navigate(user ? "/dashboard" : "/login")}
                className="text-left font-mono text-xs uppercase tracking-[0.2em] text-cyan-400"
              >
                {user ? "Dashboard" : "Client Portal"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
